import { useState, useEffect } from 'react';

import { useSocket } from 'context';
import { ButtonColors } from 'types';
import { useAppSelector } from 'store';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { isMobile, useAlert } from 'hooks';
import { joinStrings } from 'utils/helpers';
import { Button, ButtonProps } from 'components/button';
import { CloseButton } from 'components/specified-buttons';
import { selectCanExportEventReport } from 'store/slices/auth';
import { useLazyGenerateReportPdfQuery } from 'store/api/event';

import * as Styles from './styles';
import { EventReportHeaderProps } from './types';

export const EventReportHeader = ({
  date,
  title,
  event,
  onClose,
  isDailyReport,
  isEmptyReport,
}: EventReportHeaderProps) => {
  const mobile = isMobile();
  const [pdfUrl, setPdfUrl] = useState<string>();
  const [pdfJobId, setPdfJobId] = useState<string>();

  const [trigger, { isLoading }] = useLazyGenerateReportPdfQuery();
  const canExportPdf = useAppSelector(selectCanExportEventReport);

  const { showErrorAlert } = useAlert();
  const { subscribe, isConnected } = useSocket();

  // Save PDF only for the export roles (owner 18 Oct 2026); the rest of the
  // header is the same for everyone.
  const headerButtons: ButtonProps[] = canExportPdf
    ? [
        {
          text: 'Save PDF',
          icon: <Icon.Download />,
          disabled: isLoading || !!pdfJobId || isEmptyReport,
          onClick: async () => {
            if (pdfUrl) {
              window.open(pdfUrl, '_blank');
            } else {
              if (isConnected()) {
                const response = await trigger({
                  isDailyReport,
                  id: String(event.id),
                }).unwrap();
                setPdfJobId(response.jobId);
              } else {
                showErrorAlert(
                  'You have unstable network connection or offline'
                );
              }
            }
          },
        },
      ]
    : [];

  useEffect(() => {
    if (!pdfJobId) {
      return;
    }

    const timeoutId = setTimeout(() => {
      showErrorAlert('Download request timed out after 30 seconds');
      setPdfJobId(undefined);
    }, 30000);

    const unsubscribe = subscribe('pdf-url', (data) => {
      if (data.jobId === pdfJobId) {
        setPdfUrl(data.url);
        setPdfJobId(undefined);
        window.open(data.url, '_blank');
      }
    });

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [pdfJobId, subscribe, showErrorAlert]);

  const eventName = event?.name?.trim();

  return (
    // From iPad width up, Save PDF and the close button stay on the title
    // row at the right; the details take the rest of the row and the name
    // wraps inside it. Phones keep the stacked layout.
    <Flex
      gap={24}
      justifyContent="space-between"
      data-testid="event-report-header"
      flexDirection={mobile ? 'column' : 'row'}
    >
      <Flex
        flex={1}
        minWidth={0}
        gap={mobile ? 8 : 24}
        flexDirection={mobile ? 'column' : 'row'}
        alignItems={mobile ? undefined : eventName ? 'flex-start' : 'center'}
      >
        {eventName ? (
          <Styles.EventNameBlock>
            <p>{title}</p>
            <Styles.EventName>{eventName}</Styles.EventName>
          </Styles.EventNameBlock>
        ) : (
          <h2>{title}</h2>
        )}
        <Flex flexDirection="column">
          <p>Date</p>
          <h3>{date}</h3>
        </Flex>
        <Flex flexDirection="column">
          <p>Location</p>
          <h3>
            {joinStrings(
              event?.locations?.map(
                (value) => `${value?.locationType}, ${value?.eventType}`
              ) || [],
              ', '
            )}
          </h3>
        </Flex>
      </Flex>

      {(headerButtons.length > 0 || onClose) && (
        <Flex
          gap={16}
          flexShrink={0}
          alignItems={eventName ? 'flex-start' : undefined}
        >
          {headerButtons.map((button, i) => (
            <Button
              key={i}
              transparent
              padding="0"
              colorType={ButtonColors.LightBlue}
              {...button}
              text={mobile ? undefined : button.text}
              tooltip={mobile ? button.text : undefined}
            />
          ))}
          {onClose && <CloseButton onClose={onClose} />}
        </Flex>
      )}
    </Flex>
  );
};
